import type { LucideIcon } from "lucide-react";
import {
  ShieldCheck,
  GraduationCap,
  LayoutGrid,
  Network,
  BadgeDollarSign,
  CalendarClock,
  Package,
  Handshake,
  UserPlus,
  Send,
  Zap,
  UsersRound,
  Terminal,
  ReceiptText,
  Building2,
  CreditCard,
} from "lucide-react";
import { svgIcons } from "./svgIcons";
import type { UserRole } from "@/types/userType";

export type NavIcon = LucideIcon | string; // Can be Lucide icon or image URL or SVG string

// Which scope a menu belongs to, used to group the sidebar/bottom-nav for users
// who work across multiple workspaces (super admins + multi-workspace partners):
//  - 'platform'  : not tied to the selected workspace — the same regardless of
//                  which workspace is active (Workspaces, Packages, Partners,
//                  System Users, SBL-Khas). Shown under "Platform".
//  - 'workspace' : re-scopes to the currently selected workspace. Shown under
//                  "Current Workspace".
//  - undefined   : ungrouped; pinned above the sections (e.g. Overview).
// Single-workspace roles never see the section headers, so the field is inert
// for them — every item just renders in one flat list as before.
export type NavSection = "platform" | "workspace";

export interface NavItem {
  title: string;
  href: string;
  icon: NavIcon;
  items?: {
    title: string;
    href: string;
    roles?: UserRole[];
    requiredPermissions?: string[];
    // When true, the sub-item never renders in the sidebar but still
    // participates in canAccessPath's sub-item precision — used to keep a
    // stricter gate on a path whose parent group would otherwise grant it.
    hidden?: boolean;
  }[];
  iconType?: "icon" | "image" | "svg";
  // Optional array of roles that can see this item
  roles?: UserRole[];
  // Optional array of permission tags required to see this item
  requiredPermissions?: string[];
  // Optional scope group (see NavSection). Only affects the grouped rendering
  // shown to super admins / multi-workspace partners.
  section?: NavSection;
  // When true, this entry is purely a grouping container: if none of its
  // children are visible to the user, the whole entry is hidden (rather than
  // falling back to a leaf link to its href). Use for parents whose href isn't a
  // standalone page — e.g. "Learning" (Slots/Bookings/Attendance).
  groupOnly?: boolean;
  // Optional array of additional paths that should highlight this nav item as active
  activeOnPaths?: string[];
  // When true, the entry never renders in the sidebar / search, but still
  // participates in route-access decisions (canAccessPath). Use it to declare
  // who may reach pages that aren't menu items (e.g. detail pages whose access
  // differs from the parent menu).
  hidden?: boolean;
}

export const navConfig: NavItem[] = [
  {
    title: "Overview",
    href: "/portal",
    icon: svgIcons.overview,
    iconType: "svg",
    roles: ["admin", "team_manager", "learner", "super_admin", "business_admin"],
    requiredPermissions: ["ui.dashboard.menu"]
  },
  {
    // Cross-workspace central accounts + impersonation audit. Split out of the
    // workspace-scoped "Access" menu so super admins see it under "Platform".
    title: "System Users",
    href: "/portal/users/system",
    icon: UsersRound,
    iconType: "icon",
    section: "platform",
    roles: ["super_admin"],
    requiredPermissions: ["admin.menu"],
    activeOnPaths: [
      "/portal/users/system",
      "/portal/users/impersonations",
    ],
    items: [
      { title: "Users", href: "/portal/users/system", roles: ["super_admin"], requiredPermissions: ["admin.menu"] },
      { title: "Impersonation Log", href: "/portal/users/impersonations", roles: ["super_admin"], requiredPermissions: ["admin.menu"] },
    ],
  },
  {
    // Runs allowlisted backend maintenance commands. Platform-scoped: the
    // commands operate across tenants, not against the selected workspace.
    // super_admin only — the API is gated on the stricter `super.admin`
    // middleware, so a workspace admin holding admin.menu still gets a 403.
    title: "Ops Console",
    href: "/portal/admin/ops",
    icon: Terminal,
    iconType: "icon",
    section: "platform",
    roles: ["super_admin"],
    activeOnPaths: ["/portal/admin/ops"],
  },
  {
    // The invoice ledger: every invoice whatever raised it (B2C pay now, B2B
    // Finance-approved drafts, package purchases), its trail, and Xero state.
    // Platform-scoped and super_admin only, like the Ops Console.
    title: "Invoices",
    href: "/portal/admin/invoices",
    icon: ReceiptText,
    iconType: "icon",
    section: "platform",
    roles: ["super_admin"],
    activeOnPaths: ["/portal/admin/invoices"],
  },
  {
    // Every card checkout across the platform (invoice payments and website
    // package purchases), including failed and abandoned ones. Super admin.
    title: "Payments",
    href: "/portal/admin/payments",
    icon: CreditCard,
    iconType: "icon",
    section: "platform",
    roles: ["super_admin"],
    activeOnPaths: ["/portal/admin/payments"],
  },
  {
    // The current workspace's own billing: company details, its invoices
    // (drafts included), payments and receipts. Gated by the billing.menu
    // permission alone — HR Admin by default, grantable to any role (e.g. a
    // finance clerk) — so every role is listed. A learner's own invoices live
    // in My Profile → Billing instead.
    title: "Company Billing",
    href: "/portal/company-billing",
    icon: Building2,
    iconType: "icon",
    section: "workspace",
    roles: ["admin", "team_manager", "learner", "business_admin", "super_admin", "partner"],
    requiredPermissions: ["billing.menu"],
  },
  {
    // Workspace-scoped people management (learners, managers, admins, roles).
    // The platform-wide System Users + Impersonation Log live in their own menu
    // above. Note: the "/portal/users" href prefix still matches the system/
    // impersonation paths in canAccessPath, so route access is unchanged.
    title: "Access",
    href: "/portal/users",
    icon: ShieldCheck,
    iconType: "icon",
    section: "workspace",
    activeOnPaths: [
      "/portal/users/learners",
      "/portal/users/team-managers",
      "/portal/users/admins",
      "/portal/users/roles",
    ],
    roles: ["admin", "super_admin", "business_admin"],
    requiredPermissions: ["admin.menu"],
    items: [
      // Hidden from the sidebar (they render as their own "System Users" menu
      // above) but kept here so canAccessPath's sub-item precision still gates
      // them: the "/portal/users" href prefix matches these paths, so without a
      // stricter sub-entry a workspace admin/business_admin would inherit the
      // group's access to these super-admin-only pages.
      { title: "Users", href: "/portal/users/system", roles: ["super_admin"], requiredPermissions: ["admin.menu"], hidden: true },
      { title: "Learners", href: "/portal/users/learners", requiredPermissions: ["admin.menu"] },
      { title: "Managers", href: "/portal/users/team-managers", requiredPermissions: ["admin.menu"] },
      { title: "Admins", href: "/portal/users/admins", requiredPermissions: ["admin.menu"] },
      { title: "Roles", href: "/portal/users/roles", requiredPermissions: ["admin.menu"] },
      { title: "Impersonation Log", href: "/portal/users/impersonations", roles: ["super_admin"], requiredPermissions: ["admin.menu"], hidden: true },
    ],
  },
  {
    // Hidden: not a menu item. Declares that the user-detail pages
    // (/portal/users/{type}/{id}) are reachable by team managers as well as
    // admins/super admins — matching the route guard — without exposing the
    // admin-only "Access" menu above to team managers. activeOnPaths are
    // scoped to the singular detail prefixes so the listings are unaffected.
    title: "User Details",
    href: "/portal/users/detail",
    icon: ShieldCheck,
    iconType: "icon",
    hidden: true,
    roles: ["admin", "team_manager", "super_admin"],
    activeOnPaths: [
      "/portal/users/learner/",
      "/portal/users/team_manager/",
      "/portal/users/admin/",
    ],
  },
  {
    title: "Learners",
    href: "/portal/learner",
    icon: GraduationCap,
    iconType: "icon",
    section: "workspace",
    roles: ["admin", "team_manager", "business_admin"],
    requiredPermissions: ["learner.menu"],
    activeOnPaths: ["/portal/learner"],
  },
  {
    title: "Workspaces",
    href: "/portal/workspaces",
    icon: LayoutGrid,
    iconType: "icon",
    section: "platform",
    roles: ["super_admin"]
  },
  {
    title: "Partners",
    href: "/portal/partners",
    icon: Handshake,
    iconType: "icon",
    section: "platform",
    roles: ["super_admin"],
    activeOnPaths: [
      "/portal/partners",
      "/portal/partners/auto-approve",
      "/portal/partner/leads",
    ],
    items: [
      { title: "Listing", href: "/portal/partners", roles: ["super_admin"] },
      { title: "Auto-approve", href: "/portal/partners/auto-approve", roles: ["super_admin"] },
      { title: "Referral Leads", href: "/portal/partner/leads", roles: ["super_admin"] },
      { title: "Invites", href: "/portal/partner/invites", roles: ["super_admin"] },
    ],
  },
  {
    // Partners + HR admins manage their own referral leads here. Super admins
    // reach the same page via the grouped "Partners" menu above.
    title: "My Leads",
    href: "/portal/partner/leads",
    icon: UserPlus,
    iconType: "icon",
    section: "platform",
    roles: ["business_admin", "admin", "partner"]
  },
  {
    // Partners + HR admins invite people to their workspaces. Super admins reach
    // the same page via the grouped "Partners" menu above.
    title: "Invites",
    href: "/portal/partner/invites",
    icon: Send,
    iconType: "icon",
    section: "platform",
    roles: ["business_admin", "admin", "partner"]
  },
  {
    // Booking/exam auto-approve automation. Partners manage it for their own
    // attached workspaces; super admins for any. Regular HR admins are excluded
    // — the backend gate allows only super-admin or the workspace's owning
    // partner (so this is gated on "partner"/"super_admin", not "admin").
    title: "Auto-Approve",
    href: "/portal/admin/auto-approve",
    icon: Zap,
    iconType: "icon",
    section: "workspace",
    roles: ["partner", "super_admin"],
    activeOnPaths: ["/portal/admin/auto-approve"],
  },
  {
    title: "SBL-Khas",
    href: "/portal/sbl-khas",
    icon: BadgeDollarSign,
    iconType: "icon",
    section: "platform",
    roles: ["business_admin", "super_admin", "admin"],
    requiredPermissions: ["sbl_khas.menu"],
    activeOnPaths: ["/portal/sbl-khas"],
  },
  {
    title: "Organization",
    href: "/portal/admin/organizational-units",
    icon: Network,
    iconType: "icon",
    section: "workspace",
    roles: ["super_admin"],
    requiredPermissions: ["organizational_unit.menu"],
    activeOnPaths: [
      "/portal/admin/organizational-units", 
      "/portal/admin/organizational-unit-types"      
    ],
    items: [
      { title: "Units", href: "/portal/admin/organizational-units", requiredPermissions: ["organizational_unit.menu"] },
      { title: "Types", href: "/portal/admin/organizational-unit-types", requiredPermissions: ["organizational_unit.menu"] },
    ],
  },
  {
    title: "Courses",
    href: "/portal/admin/courses",
    icon: svgIcons.courses,
    iconType: "svg",
    section: "platform",
    roles: ["admin", "super_admin", "business_admin"],
    requiredPermissions: ["course.menu"],
    activeOnPaths: [
      "/portal/admin/courses",
      "/portal/admin/course/",
      "/portal/admin/course/create",
      "/portal/admin/course-pillars",
      "/portal/admin/course-pillar/",
      "/portal/admin/kit-items",
      "/portal/admin/addons",
      // Course-enrollment "learning" management (no menu item of its own) is
      // part of the admin Courses area — declare it here so route access is
      // gated to admin/super rather than left open to every authed role.
      "/portal/admin/learnings",
      "/portal/admin/learning/",
    ],
    items: [
      { title: "Listing", href: "/portal/admin/courses", requiredPermissions: ["course.menu"] },
      { title: "Pillars", href: "/portal/admin/course-pillars", requiredPermissions: ["course.menu"] },
      // Sits under Pillars because it only exists to label them.
      { title: "Pillar Types", href: "/portal/admin/course-pillar-types", requiredPermissions: ["course.menu"] },
      { title: "Kit Items", href: "/portal/admin/kit-items", requiredPermissions: ["course.menu"] },
      { title: "Add-ons", href: "/portal/admin/addons", requiredPermissions: ["course.menu"] },
    ],
  },
  {
    title: "Courses",
    href: "/portal/learning",
    icon: svgIcons.courses,
    iconType: "svg",
    roles: ["learner"],
    requiredPermissions: ["ui.learning.menu"],
    activeOnPaths: ["/portal/learning/courses", "/portal/learnings", "/portal/learning/create"],
  },
  {
    // The slot lifecycle grouped under one parent: scheduling (Slots) → learner
    // Bookings → Attendance. `groupOnly` so a permission-less HR admin (who sees
    // none of the children) doesn't get an empty entry. The parent carries NO
    // gate of its own — each child keeps its exact roles/permissions, so access
    // is identical to the three former top-level items:
    //   - Slots      : super/business (ui.learning.menu + course_enrollment.menu)
    //   - Bookings   : super/business + HR admins with admin.menu
    //   - Attendance : super/business + HR admins with admin.menu
    // Super admins bypass permissions and see all three.
    title: "Learning",
    href: "/portal/admin/learning-slots",
    icon: CalendarClock,
    iconType: "icon",
    section: "platform",
    groupOnly: true,
    roles: ["super_admin", "business_admin", "admin"],
    activeOnPaths: [
      "/portal/admin/learning-slots",
      "/portal/admin/learning-slot/",
      "/portal/admin/bookings",
      "/portal/admin/attendance",
      "/portal/admin/attendance-reviews",
    ],
    items: [
      { title: "Slots", href: "/portal/admin/learning-slots", roles: ["super_admin", "business_admin"], requiredPermissions: ["ui.learning.menu", "course_enrollment.menu"] },
      // Bookings/Attendance gated on the narrow `learning_ops.menu` (NOT admin.menu)
      // so Partners get them without the Access/user-management menu, and HR
      // admins stay out unless the permission is granted to them directly.
      { title: "Bookings", href: "/portal/admin/bookings", roles: ["super_admin", "business_admin", "admin"], requiredPermissions: ["learning_ops.menu"] },
      { title: "Attendance", href: "/portal/admin/attendance", roles: ["super_admin", "business_admin", "admin"], requiredPermissions: ["learning_ops.menu"] },
      // Attendance Reviews — approve/reject self & portal attendance. Gated on
      // the dedicated attendance.review permission (Business/Super by default;
      // HR Admin / Partner only if granted).
      { title: "Attendance Reviews", href: "/portal/admin/attendance-reviews", roles: ["super_admin", "business_admin", "admin"], requiredPermissions: ["attendance.review"] },
    ],
  },
  // {
  //   title: "My Learning",
  //   href: "/portal/mylearning",
  //   icon: svgIcons.learning,
  //   iconType: "svg",
  //   roles: ["learner"],
  //   requiredPermissions: ["ui.learning.my_learning.menu"]
  // },
  {
    title: "Certificate",
    href: "/portal/admin/certificate",
    icon: svgIcons.certificate,
    iconType: "svg",
    section: "platform",
    // System Admin tier (super_admin + business_admin) owns certificate template
    // authoring + the designer. Regular-User HR admins keep the plain
    // "Certificates" listing below.
    roles: ["super_admin", "business_admin"],
    requiredPermissions: ["certificate.menu"],
    activeOnPaths: [
      "/portal/admin/certificates",
      "/portal/admin/certificate-templates",
      "/portal/admin/certificate-template",
      "/portal/admin/certificate-template/create",
    ],
    items: [
      { title: "Listing", href: "/portal/admin/certificates", roles: ["super_admin", "business_admin"], requiredPermissions: ["certificate.view"] },
      { title: "Templates", href: "/portal/admin/certificate-templates", roles: ["super_admin", "business_admin"], requiredPermissions: ["certificate.view"] },
    ],
  },
  {
    title: "Certificates",
    href: "/portal/admin/certificates",
    icon: svgIcons.certificate,
    iconType: "svg",
    section: "workspace",
    roles: ["admin"],
    requiredPermissions: ["certificate.menu", "certificate.view"]
  },
  {
    title: "My Certificates",
    href: "/portal/certificate",
    icon: svgIcons.certificate,
    iconType: "svg",
    roles: ["learner"],
    requiredPermissions: ["certificate.menu"]
  },
  {
    // Hidden: learner-only activity pages (taking an assessment/evaluation,
    // joining a session, viewing own results, the my-learning redirect). Staff
    // review the equivalent data through the /portal/admin/... pages instead, so
    // these are locked to the learner role.
    title: "Learner activity",
    href: "/portal/_learner-activity",
    icon: svgIcons.learning,
    iconType: "svg",
    hidden: true,
    roles: ["learner"],
    activeOnPaths: [
      "/portal/mylearning",
      "/portal/assessment/",
      "/portal/evaluation/",
      "/portal/assessment-result/",
      "/portal/evaluation-result/",
      "/portal/join-session",
    ],
  },
  /* {
    title: "Reports",
    href: "/portal/report",
    icon: svgIcons.reports,
    iconType: "svg",
    roles: ["admin", "team_manager", "super_admin"]
  }, */
  {
    title: "Assessments",
    href: "/portal/assessments",
    icon: svgIcons.assessments,
    iconType: "svg",
    section: "platform",
    // Parent (the /portal/assessments view) is visible to Regular Users
    // (admin/team_manager) AND System Admins (super/business). The authoring
    // sub-items below are System-Admin-only. The /create activeOnPaths were
    // removed so they don't grant Regular Users access to authoring routes; the
    // hidden entries after this handle read vs authoring precisely.
    roles: ["admin", "team_manager", "super_admin", "business_admin"],
    requiredPermissions: ["assessment.menu"],
    activeOnPaths: [
      "/portal/admin/assessment-templates",
      "/portal/admin/assessments",
    ],
    items: [
      { title: "Listing", href: "/portal/admin/assessments", roles: ["super_admin", "business_admin"], requiredPermissions: ["assessment.menu"] },
      { title: "Templates", href: "/portal/admin/assessment-templates", roles: ["super_admin", "business_admin"], requiredPermissions: ["assessment.menu"] },
    ],
  },
  {
    // Hidden: assessment READ/operational pages (view a definition, review a
    // learner submission). Regular Users (admin/team_manager) keep these for
    // their workspace/team; System Admins too. Not learners/partners.
    title: "Assessment (read)",
    href: "/portal/admin/assessment/_read",
    icon: svgIcons.assessments,
    iconType: "svg",
    hidden: true,
    roles: ["admin", "team_manager", "super_admin", "business_admin"],
    activeOnPaths: ["/portal/admin/assessment/", "/portal/admin/assessment-submission/"],
  },
  {
    // Hidden: assessment TEMPLATE authoring (create/view/edit) — System Admin
    // (super + business) only. The /portal/admin/assessment-template/ prefix is
    // distinct from the operational /portal/admin/assessment/ prefix above.
    title: "Assessment templates (authoring)",
    href: "/portal/admin/assessment-template",
    icon: svgIcons.assessments,
    iconType: "svg",
    hidden: true,
    roles: ["super_admin", "business_admin"],
    activeOnPaths: ["/portal/admin/assessment-template/"],
  },

  {
    title: "Evaluations",
    href: "/portal/evaluations",
    icon: svgIcons.evaluations,
    iconType: "svg",
    section: "platform",
    roles: ["admin", "team_manager", "super_admin", "business_admin"],
    requiredPermissions: ["evaluation.menu"],
    activeOnPaths: [
      "/portal/admin/evaluation-templates",
      "/portal/admin/evaluations",
    ],
    items: [
      { title: "Listing", href: "/portal/admin/evaluations", roles: ["super_admin", "business_admin"], requiredPermissions: ["evaluation.menu"] },
      { title: "Templates", href: "/portal/admin/evaluation-templates", roles: ["super_admin", "business_admin"], requiredPermissions: ["evaluation.menu"] },
    ],
  },
  {
    // Hidden: evaluation READ/operational pages (view a definition, review a
    // learner submission). Regular Users (admin/team_manager) + System Admins.
    title: "Evaluation (read)",
    href: "/portal/admin/evaluation/_read",
    icon: svgIcons.evaluations,
    iconType: "svg",
    hidden: true,
    roles: ["admin", "team_manager", "super_admin", "business_admin"],
    activeOnPaths: ["/portal/admin/evaluation/", "/portal/admin/evaluation-submission/"],
  },
  {
    // Hidden: evaluation TEMPLATE authoring (create/view/edit) — System Admin only.
    title: "Evaluation templates (authoring)",
    href: "/portal/admin/evaluation-template",
    icon: svgIcons.evaluations,
    iconType: "svg",
    hidden: true,
    roles: ["super_admin", "business_admin"],
    activeOnPaths: ["/portal/admin/evaluation-template/"],
  },
  {
    title: "Calendar",
    href: "/portal/calendar",
    icon: svgIcons.calendar,
    iconType: "svg",
    section: "platform",
    roles: ["learner", "admin", "team_manager", "super_admin", "business_admin", "partner"],
    requiredPermissions: ["ui.calendar.menu"]
  },
  {
    // One "Packages" group over the two ways a package is sold: priced and put
    // in front of the public (B2C), or sold to a workspace (B2B). They were two
    // sibling menu entries, which read as unrelated features rather than two
    // halves of the same catalogue.
    //
    // groupOnly, so the parent is a container rather than a link: /portal/packages
    // is the B2B page itself and lives under the group as "Private (B2B)".
    title: "Packages",
    href: "/portal/packages",
    icon: Package,
    iconType: "icon",
    section: "platform",
    groupOnly: true,
    roles: ["super_admin"],
    activeOnPaths: ["/portal/admin/public-packages"],
    items: [
      { title: "Public (B2C)", href: "/portal/admin/public-packages", roles: ["super_admin"] },
      { title: "Private (B2B)", href: "/portal/packages", roles: ["super_admin"] },
    ],
  },
  {
    // Beside Packages because a subscription is a package sold to a workspace,
    // and chasing a renewal is the same person's job.
    //
    // Grouped the same way Packages is, for the same reason: "All" is the
    // ledger of what has been sold, "Renewals" is the worklist of what needs
    // chasing. They answer different questions off the same rows, which reads
    // clearly as two children and confusingly as two sibling menu entries.
    title: "Subscriptions",
    href: "/portal/subscriptions",
    icon: CalendarClock,
    iconType: "icon",
    section: "platform",
    groupOnly: true,
    roles: ["super_admin"],
    items: [
      { title: "All", href: "/portal/subscriptions", roles: ["super_admin"] },
      { title: "Renewals", href: "/portal/subscriptions/renewals", roles: ["super_admin"] },
    ],
  },
  {
    title: "Reports",
    href: "/portal/report",
    icon: svgIcons.reports,
    iconType: "svg",
    section: "workspace",
    roles: ["admin", "team_manager", "business_admin"],
    requiredPermissions: ["ui.report.menu"],
    activeOnPaths: ["/portal/report"],
  },
  /* Help / user guides — HIDDEN from the sidebar for now (pending stakeholder
     sign-off). The /portal/help route still exists; it's reachable via the "?"
     keyboard shortcut (see useHelpHotkey) and a link on the Profile page.
     Restore this block to bring the menu item back.
  {
    title: "Help",
    href: "/portal/help",
    icon: HelpCircle,
    iconType: "icon",
    activeOnPaths: ["/portal/help"],
  },
  */
  {
    // Hidden: not a menu item. Declares that the tester clock-shift dev tool at
    // /portal/dev/time is super-admin only, so route enforcement (canAccessPath)
    // blocks other roles from reaching it by URL. The page itself also redirects
    // when the feature isn't compiled in (prod).
    title: "Time Override",
    href: "/portal/dev/time",
    icon: Zap,
    iconType: "icon",
    hidden: true,
    roles: ["super_admin"],
    activeOnPaths: ["/portal/dev/time"],
  },
  /* {
    title: "Settings",
    href: "/portal/settings",
    icon: svgIcons.settings,
    iconType: "svg",
  } */
];